<script setup>
import { useGenerateImageVariant } from "@core/composable/useGenerateImageVariant";
import authV2ForgotPasswordIllustrationDark from "@images/pages/auth-v2-forgot-password-illustration-dark.png";
import authV2ForgotPasswordIllustrationLight from "@images/pages/auth-v2-forgot-password-illustration-light.png";
import authV2MaskDark from "@images/pages/misc-mask-dark.png";
import authV2MaskLight from "@images/pages/misc-mask-light.png";
import { VNodeRenderer } from "@layouts/components/VNodeRenderer";
import { themeConfig } from "@themeConfig";
import { useRouter } from "vue-router";
import { useConfigStore } from '@core/stores/config'
import { toast } from "vue3-toastify"
import "vue3-toastify/dist/index.css"
import axios from "@/utils/axios"

const router = useRouter();

const form = ref({
  email: "",
});

const error = ref();
const success = ref();
const resetBtn = ref(1);

// List of login images in public folder
const loginImageNames = [
  'login-thumb-1.webp',
  'login-thumb-2.webp',
  'login-thumb-3.webp',
  'login-thumb-4.webp',
]

const authThemeImg = ref("")
const randomImageKey = ref(Date.now())

// Select random image from public folder
if (loginImageNames.length) {
  const randomIndex = Math.floor(Math.random() * loginImageNames.length)
  authThemeImg.value = `/images/pages/login/${loginImageNames[randomIndex]}`
  randomImageKey.value = Date.now()
}

const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark);
const configStore = useConfigStore()
definePage({
  meta: {
    layout: "blank",
    unauthenticatedOnly: true,
  },
});

const resetPassword = async () => {
  resetBtn.value = 0;
  axios
    .post("/auth/password/reset", {
      email: form.value.email,
    })
    .then(response => {
      if (response.status == 'success') {
        useCookie("password_reset_user_email").value = form.value.email;
        toast.success(response.message, {
          autoClose: 2000,
        })
        router.push("/auth/password-reset/verify-otp");
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        })
        resetBtn.value = 1;
      }
    })
    .catch(error => {
      console.log(error)
      resetBtn.value = 1;
    })
    .finally(() => {
      resetBtn.value = 1;
    })
};
</script>

<template>
  <VRow class="auth-wrapper bg-surface" no-gutters>
    <VCol lg="8" class="d-none d-lg-flex">
      <div 
        class="position-relative bg-background w-100 me-0 login-background"
        :style="{
          backgroundImage: `url(${authThemeImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }"
        :key="randomImageKey"
      >
        <VImg class="auth-footer-mask" :src="authThemeMask" />
      </div>
    </VCol>

    <VCol cols="12" lg="4" class="d-flex align-center justify-center">
      <VCard flat :max-width="500" class="mt-12 mt-sm-0 pa-4">
        <VCardText>
          <VNodeRenderer :nodes="configStore.theme == 'dark' ? themeConfig.app.darkLogo : themeConfig.app.logo" class="mb-6" />
          <h4 class="text-h4 mb-1">Forgot Password?</h4>
          <p class="mb-0">
            Enter your email and we'll send you instructions to reset your
            password
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="resetPassword">
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  autofocus
                  label="Email"
                  type="email"
                  placeholder="johndoe@email.com"
                  :rules="[requiredValidator, emailValidator]"
                />
              </VCol>

              <VCol cols="12" v-if="error">
                <VAlert color="light-warning" class="text-warning">
                  <span class="text-lg font-weight-medium"
                    >Invalid Request</span
                  >
                  <p class="mb-0">
                    {{ error }}
                  </p>
                </VAlert>
              </VCol>

              <!-- Reset link -->
              <VCol cols="12">
                <VBtn block :disabled="resetBtn == 0" type="submit">
                  Reset Password
                </VBtn>
              </VCol>

              <!-- back to login -->
              <VCol cols="12">
                <RouterLink
                  class="d-flex align-center justify-center"
                  :to="{ name: 'login' }"
                >
                  <VIcon icon="tabler-chevron-left" class="flip-in-rtl" />
                  <span>Back to login</span>
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth.scss";

.login-background {
  min-height: 100vh;
}
</style>
